import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle/lib';
import _ from 'underscore';

import { DataSourcesResource } from '~src/datasource/resources/Datasources';
import { SearchField } from '~src/search/components/SearchField';
import { IExplorableItemTypeInfo, IFavoritableItemInfo } from '~src/ts/models/resourceInfoParts';

import { ServerUrls } from '../../../app/ServerUrls';
import { InformationObject } from '../../../app/state/InlineBannerInfoState';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { LayoutType, WidthToLayoutType } from '../../state/LayoutType';
import { SpaceInfo } from '../../state/SpaceInfo';
import { ConnectedBreadcrumbs } from '../breadcrumbs/ConnectedBreadcrumbs';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { BackButton } from './BackButton';
import { ConnectedPageHeaderMenus } from './ConnectedPageHeaderMenus';
import { PageHeaderInformation } from './PageHeaderInformation';
import { PageHeaderNavPanelButton } from './PageHeaderNavPanelButton';
import { PageHeaderSearch } from './PageHeaderSearch';
import { PageHeaderStyles } from './PageHeaderStyles';
import { VizViewerPageHeader } from './VizViewerPageHeader';

export const MAX_RESIZE_EVENTS_PER_SECOND = 5; // Need to adjust number via more testing
export const RESIZE_THROTTLE_INTERVAL = 1000 / MAX_RESIZE_EVENTS_PER_SECOND;
export const pageHeaderSearchActiveStyle = 'tb-page-header-search';

// 'light' refers to the header when viewed with a viz when the content is lighter than the background.
// With vizToolbarRedesignEnabled turned on, the header has the same color scheme between vizportal when
// viewing a viz and not, but is shorter when viewing a viz. Consider removing the type 'light' when
// removing the feature flag.
export type PageHeaderButtonType = 'light' | 'regular' | 'short';

export interface PageHeaderIconPaths {
  readonly active: string;
  readonly inactive: string;
}
export interface PageHeaderIcons {
  readonly lightIcons: PageHeaderIconPaths; // DEPRECATED remove with feature flag vizToolbarRedesignEnabled
  readonly mediumIcons: PageHeaderIconPaths;
  readonly largeIcons: PageHeaderIconPaths;
}

export interface PageHeaderStateProps {
  readonly isInVizViewerArea: boolean;
  readonly isOnSiteStatusViz: boolean;
  readonly isOnServerStatusViz: boolean;
  readonly inSearchNavLocation: boolean;
  readonly isInServerArea: boolean;
  readonly isMetricDetail: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly siteUrls: SpacesSiteUrls;
  readonly previousUrl: string;
  readonly workbookActions: AvailableActions;
  readonly displayBreadCrumbs: boolean;
  readonly layoutType: LayoutType;
  readonly searchTerm: string;
  readonly isInlineBanner: boolean;
  readonly inlineBannerInfo: InformationObject;
}

export interface PageHeaderDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly getWorkbookActions: (workbook: IExplorableItemTypeInfo) => void;
  readonly setLayoutType: (layoutType: LayoutType) => void;
}

export interface PageHeaderOwnProps  {
  readonly datasourcesResource: DataSourcesResource;
  // readonly info: InformationObject;
}

export interface PageHeaderState {
  readonly pageHeaderSearchStyle: string;
}

export type PageHeaderProps = PageHeaderStateProps & PageHeaderDispatchProps & PageHeaderOwnProps;

export class PageHeader extends React.PureComponent<PageHeaderProps> {
  public render(): JSX.Element {
    if (this.props.isInVizViewerArea) { // includes metric detail page
      return <VizViewerPageHeader {...this.props} />;

    } else if (this.props.inSearchNavLocation) {
      return <SearchPageHeader {...this.props} />;

    } else {
      return <NonVizPageHeader {...this.props} />;
    }
  }

  private onWindowResize = () => {
    const windowForDeviceWidth = WidthToLayoutType(window.innerWidth);
    this.props.setLayoutType(windowForDeviceWidth);
  }
  private throttledOnWindowResize = _.throttle(this.onWindowResize, RESIZE_THROTTLE_INTERVAL);

  public componentDidMount() {
    window.addEventListener('resize', this.throttledOnWindowResize);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.throttledOnWindowResize);
  }
}

export class NonVizPageHeader extends React.Component<PageHeaderProps, PageHeaderState> {
  public constructor(props: PageHeaderProps) {
    super(props);

    this.state = {pageHeaderSearchStyle: ''};
  }

  public render() {
    const showNavPanelButton = this.props.layoutType === 'mobile' || this.props.layoutType === 'tablet';

    return (
      <div>
        {this.props.isInlineBanner && <div className={PageHeaderStyles.InfoContainer}>
        <style>{PageHeaderStyles.infoContainerAnimationProp}</style>
        {PageHeaderInformation({info: this.props.inlineBannerInfo})}
        </div>}
      {/*  Use light header theme to set sizing variables regardless of FF. This is better for accessibility */}
      <div className={classes(PageHeaderStyles.Container, PageHeaderStyles.regularHeaderTheme)}>
      <Region>
        {showNavPanelButton && <LeftItem><PageHeaderNavPanelButton serverUrls={ServerUrls.getInstance()} addPadding={true}/></LeftItem>}
        {this.props.displayBreadCrumbs && <LeftItem><ConnectedBreadcrumbs /></LeftItem>}
      </Region>

      <Region>
        {!this.props.isInServerArea &&
          <RightItem>
            {PageHeaderSearch({headerSearchStyle: this.state.pageHeaderSearchStyle, searchClosed: this.onSearchClosed, searchClosedWithoutNeedingFocus: this.onSearchClosed, isSearchInputBoxActive: this.isSearchInputBoxActive})}
          </RightItem>
        }
        <ConnectedPageHeaderMenus buttonType="regular" showFavoritesMenu={false} />
      </Region>
      </div>
      </div>
    );
  }

  private isSearchInputBoxActive = (isActive: boolean) => {
    this.setState({pageHeaderSearchStyle: isActive ? pageHeaderSearchActiveStyle : ''});
  };

  private onSearchClosed = () => {
    // Force a render of the parent react component to get the
    // Omnibox to update.
    this.forceUpdate();
  }
}

export class SearchPageHeader extends React.Component<PageHeaderProps> {
  public render() {
    const showNavPanelButton = this.props.layoutType === 'mobile' || this.props.layoutType === 'tablet';
    return (
      <div>
      {this.props.isInlineBanner && <div className={PageHeaderStyles.InfoContainer}>
      <style>{PageHeaderStyles.infoContainerAnimationProp}</style>
        {PageHeaderInformation({info: this.props.inlineBannerInfo})}
        </div>}
      <div className={classes(PageHeaderStyles.Container, PageHeaderStyles.regularHeaderTheme)}>
        {showNavPanelButton && <Region><LeftItem><PageHeaderNavPanelButton serverUrls={ServerUrls.getInstance()} addPadding={false} /></LeftItem></Region>}
          <BackButton
            previousUrl={this.props.previousUrl || this.props.siteUrls.defaultUrl()} // default to user's start page
            parentId={''}
            siteUrls={this.props.siteUrls}
            serverUrls={ServerUrls.getInstance()}
            isOnDatasourceAnalyticsViz={false}
            isOnSiteStatusViz={false}
            isOnServerStatusViz={false}
            isMetricDetail={false}
            buttonType={'regular'}
            styleOverrides={{
              marginLeft: TabStyles.Spacing.ExtraLargeSpace,
              marginRight: TabStyles.Spacing.ExtraLargeSpace,
            }}
            wrapperDivStyleOverrides={{ marginTop: '0px'}}
          />
          <SearchField searchTerm={this.props.searchTerm} />
        <Region styleOverrides={{paddingRight:'10px'}}>
          <ConnectedPageHeaderMenus buttonType="regular" showFavoritesMenu={false} />
        </Region>
      </div>
      </div>
    );
  }
}

export function Region(props: { children: React.ReactNode, styleOverrides?: React.CSSProperties }) {
  return (
    <div className={PageHeaderStyles.Region} style={{...props.styleOverrides}}>
      {props.children}
    </div>
  );
}

export function LeftItem(props: { children: React.ReactNode, className?: string }) {
  return (
    <div className={classes(PageHeaderStyles.LeftItem, props.className)}>
      {props.children}
    </div>
  );
}

export function RightItem(props: { children: React.ReactNode, className?: string }) {
  return (
    <div className={classes(PageHeaderStyles.RightItem, props.className)}>
      {props.children}
    </div>
  );
}
